import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { MButton } from "../atoms/MButtons";
import { IconX, IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { wizardConfigs } from "@/config/wizards";
import { MWizardProps } from "@/types/wizard";
import styles from "./MWizard.module.css";
import {
  calculatePopoverPosition,
  calculateArrowPosition,
  removeHighlightClasses,
} from "@/utils/wizard-utils";

export const MWizard: React.FC<MWizardProps> = ({ page, onComplete }) => {
  const { t } = useTranslation();
  const wizardConfig = wizardConfigs[page];
  const config = wizardConfig?.steps || [];

  //  fall back to page name
  const wizardKey = useMemo(
    () => wizardConfig?.key || page,
    [page, wizardConfig?.key]
  );

  const [isOpen, setIsOpen] = useState(true); // Start open when component mounts
  const [currentStep, setCurrentStep] = useState(0);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState({
    left: "50%",
    top: "50%",
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const currentTargetRef = useRef<HTMLElement | null>(null);
  const stickyHeaderRef = useRef<HTMLElement | null>(null);

  const calculatePosition = useCallback(() => {
    if (!isOpen || currentStep >= config.length) return;

    const stepConfig = config[currentStep];
    const targetElement = document.querySelector(
      stepConfig.target
    ) as HTMLElement;

    if (!targetElement) {
      console.warn(`Target element not found: ${stepConfig.target}`);
      return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    const popoverElement = popoverRef.current;

    if (!popoverElement) return;

    // Remove highlight classes from previous target
    if (
      currentTargetRef.current &&
      currentTargetRef.current !== targetElement
    ) {
      removeHighlightClasses(currentTargetRef.current);
    }

    // Restore sticky positioning to previous header if any
    if (stickyHeaderRef.current) {
      stickyHeaderRef.current.style.position = "";
      stickyHeaderRef.current.style.zIndex = "";
      stickyHeaderRef.current = null;
    }

    const elementToHighlight = targetElement;

    // Check if target is inside a sticky MHeader and temporarily remove sticky positioning
    let ancestor = elementToHighlight.parentElement;
    while (ancestor && ancestor !== document.body) {
      const ancestorStyle = window.getComputedStyle(ancestor);

      if (
        ancestorStyle.position === "sticky" ||
        ancestorStyle.position === "fixed"
      ) {
        const hasHeaderClass =
          ancestor.className.includes("headerContainer") ||
          ancestor.className.includes("stickyHeader") ||
          ancestorStyle.backgroundImage?.includes("gradient") ||
          ancestorStyle.background?.includes("gradient");

        if (hasHeaderClass) {
          stickyHeaderRef.current = ancestor;
          ancestor.style.position = "relative";
          ancestor.style.zIndex = "auto";
        } else {
          ancestor.classList.add("wizard-ancestor-boost");
        }
      }
      ancestor = ancestor.parentElement;
    }

    currentTargetRef.current = elementToHighlight;
    elementToHighlight.classList.add("wizard-highlight");
    const highlightComputedPosition =
      window.getComputedStyle(elementToHighlight).position;
    if (!highlightComputedPosition || highlightComputedPosition === "static") {
      elementToHighlight.classList.add("wizard-highlight-relative");
      elementToHighlight.style.position = "relative";
    }
    elementToHighlight.style.zIndex = "10001";

    const popoverRect = popoverElement.getBoundingClientRect();
    const placement = stepConfig.placement || "bottom";

    // Calculate popover position
    const { top: adjustedTop, left: adjustedLeft } = calculatePopoverPosition(
      targetRect,
      popoverRect,
      placement
    );

    const arrowTargetElement = stepConfig.arrowTarget
      ? document.querySelector(stepConfig.arrowTarget)
      : targetElement;

    const arrowPos = arrowTargetElement
      ? calculateArrowPosition(
          arrowTargetElement,
          adjustedLeft,
          adjustedTop,
          popoverRect,
          placement
        )
      : { left: "50%", top: "50%" };

    setPopoverPosition({ top: adjustedTop, left: adjustedLeft });
    setArrowPosition(arrowPos);

    // Scroll target into view
    targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentStep, config, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const wizardPageClass = `wizard-page-${wizardKey}`;
      document.body.classList.add("wizard-active", wizardPageClass);
      calculatePosition();
      window.addEventListener("resize", calculatePosition);
      window.addEventListener("scroll", calculatePosition, true);

      return () => {
        document.body.classList.remove("wizard-active", wizardPageClass);
        window.removeEventListener("resize", calculatePosition);
        window.removeEventListener("scroll", calculatePosition, true);

        // Remove highlight classes on cleanup
        removeHighlightClasses(currentTargetRef.current);

        // Restore header stacking context if we modified it
        if (stickyHeaderRef.current) {
          stickyHeaderRef.current.style.position = "";
          stickyHeaderRef.current.style.zIndex = "";
          stickyHeaderRef.current = null;
        }
      };
    }
  }, [isOpen, calculatePosition, wizardKey]);

  const handleNext = () => {
    setIsAnimating(true);

    setTimeout(() => {
      if (currentStep < config.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleComplete();
      }
      setIsAnimating(false);
    }, 300);
  };

  const handleComplete = () => {
    removeHighlightClasses(currentTargetRef.current);
    // Restore sticky positioning to header if any
    if (stickyHeaderRef.current) {
      stickyHeaderRef.current.style.position = "";
      stickyHeaderRef.current.style.zIndex = "";
      stickyHeaderRef.current = null;
    }
    setIsOpen(false);
    setCurrentStep(0);
    onComplete?.();
  };

  const handleSkip = () => {
    removeHighlightClasses(currentTargetRef.current);
    // Restore sticky positioning to header if any
    if (stickyHeaderRef.current) {
      stickyHeaderRef.current.style.position = "";
      stickyHeaderRef.current.style.zIndex = "";
      stickyHeaderRef.current = null;
    }
    setIsOpen(false);
    setCurrentStep(0);
    onComplete?.();
  };

  if (!isOpen || config.length === 0) return null;

  const stepConfig = config[currentStep];
  const translationKey = `wizard.${wizardKey}.${stepConfig.translationKey}`;

  return (
    <>
      {/*  overlay */}
      <div className={styles.overlay} />

      {/* Tooltip/Modal */}
      <div
        ref={popoverRef}
        className={`${styles.tooltip} ${isAnimating ? styles.animating : ""}`}
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
        }}
      >
        <div
          className={styles.tooltipContent}
          data-placement={stepConfig.placement || "bottom"}
          style={
            {
              "--arrow-left": arrowPosition.left,
              "--arrow-top": arrowPosition.top,
            } as React.CSSProperties
          }
        >
          {/* Close button */}
          <div className={styles.closeButton} aria-label="Close">
            <IconX onClick={handleSkip} size={20} color="#383838" />
          </div>

          {/* Step indicator */}
          <div
            className={`${styles.stepIndicator} ${
              isAnimating ? styles.animating : ""
            }`}
          >
            {currentStep + 1}/{config.length}
          </div>

          {/* Content */}
          <div
            className={`${styles.contentWrapper} ${
              isAnimating ? styles.animating : ""
            }`}
          >
            <p className={styles.contentText}>
              {t(`${translationKey}.content`)}
            </p>
          </div>

          <div
            className={`${styles.buttonWrapper} ${
              isAnimating ? styles.animating : ""
            }`}
          >
            <MButton
              variant="link"
              size="sm"
              rightIcon={
                currentStep === config.length - 1 ? "" : <IconChevronRight />
              }
              text={
                currentStep === config.length - 1
                  ? t("wizard.buttons.done")
                  : t("wizard.buttons.next")
              }
              onClick={handleNext}
              disabled={isAnimating}
              className={styles.stepButton}
            />
          </div>
        </div>
      </div>
    </>
  );
};
